import { Observable } from 'rxjs';
import { $httpClient } from '@/services/httpClient';
import {
  CREATE_MANAGER,
  CREATE_REMINDERS,
  DELETE_MANAGER,
  DELETE_REMINDERS,
  EXPORT_FORM_101,
  GET_ALL_CUSTOMERS,
  GET_ALL_OFFICES_BY_CUSTOMER,
  GET_ALL_REMINDERS_BY_CUSTOMER,
  GET_ALL_SECTIONS_BY_OFFICE,
  GET_CUSTOMER_DETAILS,
  GET_MANAGER_PRIVILEGES,
  GET_MANAGERS_BY_CUSTOMER,
  GET_OFFICE_DETAILS,
  LIST_ORG_UNITS,
  SEARCH_MANAGER,
  UPDATE_CUSTOMER,
  UPDATE_MANAGER,
  UPDATE_OFFICE,
  UPLOAD_MANAGER_PRIVILEGES_FILE,
  UPLOAD_MANAGER_PRIVILEGES_FILE_UNIT,
} from '@/types/customer.types';
import { CustomerDetailsModel } from '@/models/backoffice/CustomerDetails';
import { AxiosResponse } from 'axios';
import { OfficesModel } from '@/models/Offices.model';
import { CreateMailReminderModel } from '@/models/backoffice/CreateMailReminderModel';
import { BaseResponse } from '@/models/response/base/BaseResponse';
import { GridResponse } from '@/models/response/base/GridResponse';
import { DeleteRemindersRequest } from '@/models/backoffice/DeleteRemindersRequest';
import { CreateRemindersRequest } from '@/models/backoffice/CreateRemindersRequest';
import { ManagerPrivilegesModel } from '@/models/backoffice/ManagerPrivilegesModel';
import { ManagerPrivilegesRequest } from '@/models/backoffice/ManagerPrivilegesRequest';
import { SearchManagerRequest } from '@/models/backoffice/SearchManagerRequest';
import { CreateManagerPrivilegesModel } from '@/models/backoffice/CreateManagerPrivilegesModel';
import { SearchManagerModel } from '@/models/backoffice/SearchManagerModel';
import { OrganisationUnitsRequestModel } from '@/models/backoffice/OrganisationUnitsRequestModel';
import { ExportForm101RequestModel } from '@/models/backoffice/ExportForm101RequestModel';
import { InputFile } from '@/models/InputFile.model';
import { SectionsModel } from '@/models/Sections.model';

export default class CustomerService {
  private readonly baseApi: string;
  private readonly customerBaseApi: string;
  private readonly remindersBaseApi: string;
  private readonly managersBaseApi: string;
  private readonly reportsBaseApi: string;

  constructor() {
    this.baseApi = 'api/bo/';
    this.customerBaseApi = this.baseApi + 'customer/';
    this.remindersBaseApi = this.baseApi + 'reminders/';
    this.managersBaseApi = this.baseApi + 'manager/';
    this.reportsBaseApi = this.baseApi + 'report/';
  }

  public getAllCustomers(): Observable<GridResponse<CustomerDetailsModel>> {
    return $httpClient.get <GridResponse<CustomerDetailsModel>>(this.customerBaseApi + GET_ALL_CUSTOMERS);
  }

  public getCustomerDetails(indexNum: number): Observable<CustomerDetailsModel> {
    return $httpClient.get<CustomerDetailsModel>(this.customerBaseApi + GET_CUSTOMER_DETAILS + '?indexNum=' + indexNum);
  }

  public updateCustomer(CustomerDetailsAsCustomerModel: CustomerDetailsModel): Observable<AxiosResponse> {
    return $httpClient.put(this.customerBaseApi + UPDATE_CUSTOMER, CustomerDetailsAsCustomerModel);
  }

  public getAllOfficesByCustomer(indexNum: number): Observable<GridResponse<OfficesModel>> {
    return $httpClient.get<GridResponse<OfficesModel>>(this.customerBaseApi +
      GET_ALL_OFFICES_BY_CUSTOMER + '?indexNum=' + indexNum);
  }

  public getAllSectionsByOffice(officeNum: number): Observable<GridResponse<SectionsModel>> {
    return $httpClient.post<GridResponse<SectionsModel>>(this.reportsBaseApi +
      GET_ALL_SECTIONS_BY_OFFICE + '?officeNum=' + officeNum);
  }

  public getOfficeDetails(indexNum: number, officeNum: number): Observable<CustomerDetailsModel> {
    return $httpClient.get<CustomerDetailsModel>(this.customerBaseApi + GET_OFFICE_DETAILS +
      '?indexNum=' + indexNum + '&officeNum=' + officeNum);
  }

  public updateOffice(data: CustomerDetailsModel): Observable<BaseResponse> {
    return $httpClient.post<BaseResponse>(this.customerBaseApi + UPDATE_OFFICE, data);
  }

  public getAllRemindersByCustomer(indexNum: number): Observable<GridResponse<OfficesModel>> {
    return $httpClient.get<GridResponse<OfficesModel>>(this.remindersBaseApi +
      GET_ALL_REMINDERS_BY_CUSTOMER + '?indexNum=' + indexNum);
  }

  public createReminders(createReminders: CreateRemindersRequest): Observable<CreateMailReminderModel> {
    return $httpClient.post<CreateMailReminderModel>(this.remindersBaseApi + CREATE_REMINDERS, createReminders);
  }

  public deleteReminders(deleteRemindersRequest: DeleteRemindersRequest): Observable<any> {
    return $httpClient.post<any>(this.remindersBaseApi + DELETE_REMINDERS, deleteRemindersRequest);
  }

  public getManagersByCustomer(indexNum: number): Observable<GridResponse<ManagerPrivilegesModel>> {
    return $httpClient.get<GridResponse<ManagerPrivilegesModel>>(this.managersBaseApi +
      GET_MANAGERS_BY_CUSTOMER + '?indexNum=' + indexNum);
  }

  public searchManager(searchManagerRequest: SearchManagerRequest): Observable<SearchManagerModel> {
    return $httpClient.post<SearchManagerModel>(this.managersBaseApi + SEARCH_MANAGER, searchManagerRequest);
  }

  public createManager(managerPrivilegesModel: CreateManagerPrivilegesModel): Observable<BaseResponse> {
    return $httpClient.post<BaseResponse>(this.managersBaseApi + CREATE_MANAGER, managerPrivilegesModel);
  }

  public deleteManager(managerToDelete: ManagerPrivilegesRequest): Observable<boolean> {
    return $httpClient.post<boolean>(this.managersBaseApi + DELETE_MANAGER, managerToDelete);
  }

  public listOrgUnits(indexNum: number): Observable<GridResponse<ManagerPrivilegesModel>> {
    return $httpClient.get<GridResponse<ManagerPrivilegesModel>>(this.managersBaseApi +
      GET_MANAGERS_BY_CUSTOMER + '?indexNum=' + indexNum);
  }

  public getManagerPrivileges(managerToUpdate: ManagerPrivilegesRequest):
    Observable<GridResponse<ManagerPrivilegesModel>> {
    return $httpClient.post<GridResponse<ManagerPrivilegesModel>>(this.managersBaseApi +
      GET_MANAGER_PRIVILEGES, { indexNum: managerToUpdate.indexNum, id: managerToUpdate.id });
  }

  public updateManagerPrivileges(managerToUpdate: ManagerPrivilegesRequest):
    Observable<GridResponse<ManagerPrivilegesModel>> {
    return $httpClient.post<GridResponse<ManagerPrivilegesModel>>(this.managersBaseApi +
      UPDATE_MANAGER, managerToUpdate);
  }

  public getOrganisationUnitsList(organisationUnitsRequest: OrganisationUnitsRequestModel):
    Observable<GridResponse<ManagerPrivilegesModel>> {
    return $httpClient.post<GridResponse<ManagerPrivilegesModel>>(this.managersBaseApi +
      LIST_ORG_UNITS, organisationUnitsRequest);
  }

  public createExportForm101Request(exportForm101Request: ExportForm101RequestModel): Observable<BaseResponse> {
    return $httpClient.post<BaseResponse>(this.reportsBaseApi + EXPORT_FORM_101, exportForm101Request);
  }
  public uploadManagerPrivilegesFile(
    file: InputFile, indexNum: string, replace: boolean, type: string,
  ):
    Observable<BaseResponse> {
    const formData = new FormData();
    formData.append('file', file.file);
    formData.append('indexNum', indexNum);
    formData.append('replace', JSON.stringify(replace));
    return $httpClient.post(this.managersBaseApi + (type === 'UPLOAD_MANAGER_PRIVILEGES' ? UPLOAD_MANAGER_PRIVILEGES_FILE : UPLOAD_MANAGER_PRIVILEGES_FILE_UNIT), formData);
  }
}


